/**
 * Organization Domain Governance Page
 *
 * Allows owners/admins to:
 *  - Claim a domain for DNS verification
 *  - Verify ownership via TXT record
 *  - Set governance policy (none / auto_enroll)
 *  - Preview and retroactively enroll existing users
 *  - Remove a domain claim
 */

import type { JSX } from 'react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDisclosure } from '@heroui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Copy } from 'lucide-react';
import { z } from 'zod';

import { useTenant } from '~/hooks/useTenant';
import {
  claimDomain,
  deleteDomain,
  enrollExistingUsers,
  listDomains,
  type OrgDomain,
  previewEnrollment,
  updateGovernancePolicy,
  verifyDomain,
} from '~/services/api/organizations';

import { ConfirmationDialog } from '~/components/ConfirmationDialog';

function CopyButton({ value }: { value: string }): JSX.Element {
  const [copied, setCopied] = useState(false);

  const handleCopy = (): void => {
    void navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="shrink-0 text-default-400 hover:text-foreground transition-colors"
      aria-label="Copy to clipboard"
    >
      {copied ? <Check size={14} className="text-success-500" /> : <Copy size={14} />}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Validation schema
// ---------------------------------------------------------------------------

const claimSchema = z.object({
  domain: z
    .string()
    .min(4, 'Enter a valid domain')
    .regex(
      /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/,
      'Must be a valid domain name (e.g. acme.com)',
    ),
});

type ClaimFormValues = z.infer<typeof claimSchema>;

// ---------------------------------------------------------------------------
// Sub-component: single domain card
// ---------------------------------------------------------------------------

interface DomainCardProps {
  domain: OrgDomain;
  orgId: string;
}

const PAGE_SIZE = 10;

function DomainCard({ domain, orgId }: DomainCardProps): JSX.Element {
  const queryClient = useQueryClient();
  const [previewOffset, setPreviewOffset] = useState(0);
  const [invitedUids, setInvitedUids] = useState<Set<string>>(new Set());
  const [selectedUids, setSelectedUids] = useState<Set<string>>(new Set());
  // null = closed; undefined = invite all; string[] = invite selected subset
  const [inviteTarget, setInviteTarget] = useState<string[] | undefined | null>(null);
  const { isOpen: isDeleteOpen, onClose: onDeleteClose, onOpen: onDeleteOpen } = useDisclosure();

  const verifyMutation = useMutation({
    mutationFn: () => verifyDomain(orgId, domain.id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['domains', orgId] }),
  });

  const policyMutation = useMutation({
    mutationFn: (policy: 'none' | 'auto_enroll') =>
      updateGovernancePolicy(orgId, domain.id, policy),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['domains', orgId] }),
  });

  const inviteMutation = useMutation({
    mutationFn: (userUids?: string[]) => enrollExistingUsers(orgId, domain.id, userUids),
    onSuccess: (_data, userUids) => {
      if (userUids?.length === 1) {
        setInvitedUids(prev => new Set([userUids[0], ...prev]));
      } else {
        setSelectedUids(new Set());
        void queryClient.invalidateQueries({ queryKey: ['domain-preview', orgId, domain.id] });
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteDomain(orgId, domain.id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['domains', orgId] }),
  });

  const { data: preview, isLoading: previewLoading } = useQuery({
    queryKey: ['domain-preview', orgId, domain.id, previewOffset],
    queryFn: () => previewEnrollment(orgId, domain.id, previewOffset, PAGE_SIZE),
    enabled: domain.status === 'verified',
  });

  const statusColor: Record<string, string> = {
    pending_verification: 'text-warning-600 dark:text-warning-400',
    verified: 'text-success-600 dark:text-success-400',
    revoked: 'text-default-400',
  };

  const statusLabel: Record<string, string> = {
    pending_verification: 'Pending verification',
    verified: 'Verified',
    revoked: 'Revoked',
  };

  return (
    <div className="border border-default rounded-lg p-4 space-y-3 bg-content1">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="font-semibold text-foreground">{domain.domain}</p>
          <p className={`text-sm ${statusColor[domain.status] ?? 'text-default-400'}`}>
            {statusLabel[domain.status] ?? domain.status}
          </p>
        </div>
        <button
          type="button"
          onClick={onDeleteOpen}
          disabled={deleteMutation.isPending}
          className="text-danger text-sm hover:underline disabled:opacity-50"
        >
          Remove
        </button>
      </div>

      {/* DNS instructions while pending */}
      {domain.status === 'pending_verification' && domain.verification_token && (
        <div className="bg-default-50 dark:bg-default-100/10 rounded p-3 space-y-2">
          <p className="text-sm text-foreground/80 font-medium">Add this TXT record to your DNS:</p>
          <div className="space-y-1">
            <div className="flex gap-2 text-xs text-default-500">
              <span className="w-12 shrink-0">Host</span>
              <code className="font-mono">@</code>
            </div>
            <div className="flex gap-2 text-xs text-default-500 items-baseline">
              <span className="w-12 shrink-0">Value</span>
              <span className="inline-flex items-baseline gap-1 flex-wrap">
                <code className="font-mono break-all text-foreground">
                  {domain.verification_token}
                </code>
                <CopyButton value={domain.verification_token} />
              </span>
            </div>
          </div>
          <p className="text-xs text-default-400">
            DNS propagation can take up to 48h. Token expires{' '}
            {domain.verification_token_expires_at
              ? new Date(domain.verification_token_expires_at).toLocaleDateString()
              : 'soon'}
            .
          </p>
          <button
            type="button"
            onClick={() => verifyMutation.mutate()}
            disabled={verifyMutation.isPending}
            className="mt-1 px-3 py-1.5 text-sm bg-primary text-white rounded hover:bg-primary/90 disabled:opacity-50"
          >
            {verifyMutation.isPending ? 'Checking…' : 'Verify now'}
          </button>
          {verifyMutation.data && !verifyMutation.data.success && (
            <p className="text-sm text-danger-600 dark:text-danger-400">
              {verifyMutation.data.message}
            </p>
          )}
          {domain.verify_attempt_count >= 8 && (
            <p className="text-xs text-default-400">
              {10 - domain.verify_attempt_count} attempt
              {10 - domain.verify_attempt_count !== 1 ? 's' : ''} remaining this hour.
            </p>
          )}
        </div>
      )}

      {/* Policy + enrollment controls once verified */}
      {domain.status === 'verified' && (
        <div className="space-y-4">
          {/* Policy cards */}
          <div>
            <p className="text-sm font-medium text-foreground/80 mb-2">Enrollment policy</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {(
                [
                  {
                    value: 'none',
                    label: 'Admin-controlled',
                    description:
                      'Nothing happens automatically. Admins add members by direct invitation or by discovering domain users below and selecting who to invite.',
                  },
                  {
                    value: 'auto_enroll',
                    label: 'Auto-enroll',
                    description:
                      'Anyone who signs in with a matching email domain is automatically added as a member. Best for orgs where everyone on the domain should have access.',
                  },
                ] as const
              ).map(option => {
                const isActive = domain.governance_policy === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    disabled={policyMutation.isPending}
                    onClick={() => {
                      if (!isActive) policyMutation.mutate(option.value);
                    }}
                    className={[
                      'text-left rounded-lg border p-3 transition-colors disabled:opacity-50',
                      isActive
                        ? 'border-primary bg-primary/5'
                        : 'border-default hover:border-default-400 bg-content1',
                    ].join(' ')}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <span
                        className={[
                          'w-3.5 h-3.5 rounded-full border-2 shrink-0',
                          isActive ? 'border-primary bg-primary' : 'border-default-400',
                        ].join(' ')}
                      />
                      <span
                        className={`text-sm font-medium ${isActive ? 'text-primary' : 'text-foreground'}`}
                      >
                        {option.label}
                      </span>
                    </div>
                    <p className="text-xs text-default-500 leading-relaxed pl-5">
                      {option.description}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* People from your domain */}
          <div className="border-t border-default pt-3 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-baseline gap-2">
                <p className="text-sm font-medium text-foreground/80">People from your domain</p>
                {preview && preview.pagination.total > 0 && (
                  <span className="text-xs text-default-400">
                    {preview.pagination.total} account
                    {preview.pagination.total !== 1 ? 's' : ''} found
                  </span>
                )}
              </div>
              {preview && preview.pagination.total > 0 && (
                <div className="flex items-center gap-3 shrink-0">
                  {selectedUids.size > 0 && (
                    <button
                      type="button"
                      onClick={() => setInviteTarget([...selectedUids])}
                      disabled={inviteMutation.isPending}
                      className="text-xs text-primary hover:underline disabled:opacity-50"
                    >
                      Invite selected ({selectedUids.size})
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setInviteTarget(undefined)}
                    disabled={inviteMutation.isPending}
                    className="text-xs text-primary hover:underline disabled:opacity-50"
                  >
                    Invite all
                  </button>
                </div>
              )}
            </div>

            {previewLoading && <p className="text-sm text-default-400">Loading…</p>}

            {preview && preview.pagination.total === 0 && !previewLoading && (
              <p className="text-sm text-default-400">
                {preview.already_members > 0
                  ? `All ${preview.already_members} domain account${preview.already_members !== 1 ? 's' : ''} are already members.`
                  : 'No other accounts found for this domain yet.'}
              </p>
            )}

            {preview && preview.users_to_enroll.length > 0 && (
              <div className="space-y-0">
                {/* Select-all row */}
                {((): JSX.Element => {
                  const selectableUids = preview.users_to_enroll
                    .filter(u => !invitedUids.has(u.uid))
                    .map(u => u.uid);
                  const allSelected =
                    selectableUids.length > 0 && selectableUids.every(uid => selectedUids.has(uid));
                  const someSelected = selectableUids.some(uid => selectedUids.has(uid));
                  return (
                    <div className="flex items-center gap-3 py-1.5 border-b border-default">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        ref={el => {
                          if (el) el.indeterminate = someSelected && !allSelected;
                        }}
                        onChange={() => {
                          setSelectedUids(prev => {
                            const next = new Set(prev);
                            if (allSelected) {
                              selectableUids.forEach(uid => next.delete(uid));
                            } else {
                              selectableUids.forEach(uid => next.add(uid));
                            }
                            return next;
                          });
                        }}
                        className="accent-primary"
                        aria-label="Select all on this page"
                      />
                      <span className="text-xs text-default-400">
                        {allSelected ? 'Deselect all on page' : 'Select all on page'}
                      </span>
                    </div>
                  );
                })()}

                {preview.users_to_enroll.map(u => {
                  const isInvited = invitedUids.has(u.uid);
                  const isSelected = selectedUids.has(u.uid);
                  return (
                    <div
                      key={u.uid}
                      className="flex items-center gap-3 py-2 border-b border-default last:border-0"
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        disabled={isInvited}
                        onChange={() => {
                          setSelectedUids(prev => {
                            const next = new Set(prev);
                            if (isSelected) next.delete(u.uid);
                            else next.add(u.uid);
                            return next;
                          });
                        }}
                        className="accent-primary shrink-0 disabled:opacity-30"
                        aria-label={`Select ${u.display_name ?? u.email}`}
                      />
                      <div className="min-w-0 flex-1">
                        {u.display_name && (
                          <p className="text-sm text-foreground truncate">{u.display_name}</p>
                        )}
                        <p className="text-xs text-default-400 truncate">{u.email}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => inviteMutation.mutate([u.uid])}
                        disabled={isInvited || inviteMutation.isPending}
                        className="text-xs shrink-0 disabled:opacity-50 text-primary hover:underline"
                      >
                        {isInvited ? 'Invited ✓' : 'Send invite'}
                      </button>
                    </div>
                  );
                })}

                {/* Pagination */}
                {preview.pagination.total > PAGE_SIZE && (
                  <div className="flex items-center justify-between pt-2 text-xs text-default-400">
                    <button
                      type="button"
                      onClick={() => setPreviewOffset(o => Math.max(0, o - PAGE_SIZE))}
                      disabled={previewOffset === 0}
                      className="hover:text-foreground disabled:opacity-30"
                    >
                      ← Previous
                    </button>
                    <span>
                      {previewOffset + 1}–
                      {Math.min(previewOffset + PAGE_SIZE, preview.pagination.total)} of{' '}
                      {preview.pagination.total}
                    </span>
                    <button
                      type="button"
                      onClick={() => setPreviewOffset(o => o + PAGE_SIZE)}
                      disabled={!preview.pagination.has_more}
                      className="hover:text-foreground disabled:opacity-30"
                    >
                      Next →
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Footer counts */}
            {preview && (preview.already_members > 0 || preview.pending_consent > 0) && (
              <p className="text-xs text-default-400 pt-1">
                {[
                  preview.already_members > 0 &&
                    `${preview.already_members} already ${preview.already_members === 1 ? 'is a member' : 'are members'}`,
                  preview.pending_consent > 0 &&
                    `${preview.pending_consent} invitation${preview.pending_consent !== 1 ? 's' : ''} pending`,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
            )}

            {inviteMutation.isSuccess && !inviteMutation.variables && (
              <p className="text-xs text-success-600 dark:text-success-400">
                Invitations sent to {inviteMutation.data.enrolled} user
                {inviteMutation.data.enrolled !== 1 ? 's' : ''}.
                {inviteMutation.data.re_enrolled > 0 &&
                  ` Re-invited ${inviteMutation.data.re_enrolled}.`}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Error display */}
      {(verifyMutation.isError ||
        policyMutation.isError ||
        deleteMutation.isError ||
        inviteMutation.isError) && (
        <p className="text-sm text-danger-600 dark:text-danger-400">
          {(
            (verifyMutation.error ??
              policyMutation.error ??
              deleteMutation.error ??
              inviteMutation.error) as Error
          )?.message ?? 'An error occurred.'}
        </p>
      )}

      <ConfirmationDialog
        isOpen={isDeleteOpen}
        onClose={onDeleteClose}
        onConfirm={() => {
          deleteMutation.mutate();
        }}
        title="Remove domain"
        content={
          <p>
            Remove <strong>{domain.domain}</strong>? Any pending invitations will be cancelled.
            Active members keep their access. You can re-claim and re-verify the domain at any time.
          </p>
        }
        confirmText="Remove"
        isPending={deleteMutation.isPending}
      />

      <ConfirmationDialog
        isOpen={inviteTarget !== null}
        onClose={() => setInviteTarget(null)}
        onConfirm={() => {
          inviteMutation.mutate(inviteTarget ?? undefined);
        }}
        confirmColor="primary"
        title={
          inviteTarget === undefined ? 'Invite all' : `Invite ${(inviteTarget ?? []).length} people`
        }
        content={
          <div className="space-y-2">
            <p>
              {inviteTarget === undefined ? (
                <>
                  This will invite all <strong>{preview?.pagination.total ?? 0}</strong> people from{' '}
                  <strong>{domain.domain}</strong> who have accounts but are not yet members of this
                  organization.
                </>
              ) : (
                <>
                  This will invite the <strong>{(inviteTarget ?? []).length}</strong> selected{' '}
                  {(inviteTarget ?? []).length === 1 ? 'person' : 'people'} from{' '}
                  <strong>{domain.domain}</strong> to this organization.
                </>
              )}
            </p>
            <p className="text-default-500 text-sm">
              Each person will receive an email and must accept before they appear as a member.
              Anyone who declines or ignores the invitation won&apos;t be added.
            </p>
          </div>
        }
        confirmText="Send invites"
        isPending={inviteMutation.isPending}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page component
// ---------------------------------------------------------------------------

export function OrganizationDomains(): JSX.Element {
  const { currentOrg } = useTenant();
  const queryClient = useQueryClient();

  const {
    formState: { errors },
    handleSubmit,
    register,
    reset,
  } = useForm<ClaimFormValues>({ resolver: zodResolver(claimSchema) });

  const orgId = currentOrg?.id ?? '';

  const { data, isLoading } = useQuery({
    queryKey: ['domains', orgId],
    queryFn: () => listDomains(orgId),
    enabled: !!orgId,
  });

  const claimMutation = useMutation({
    mutationFn: (values: ClaimFormValues) => claimDomain(orgId, values.domain),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['domains', orgId] });
      reset();
    },
  });

  const onSubmit = (values: ClaimFormValues): void => {
    claimMutation.mutate(values);
  };

  if (!orgId) return <p className="text-default-400">No organization selected.</p>;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-foreground">Domain Governance</h2>
        <p className="text-sm text-default-500 mt-1">
          Verify domain ownership to govern sign-ins and automatically enroll users whose email
          matches your domain.
        </p>
      </div>

      {/* Claim form */}
      <form
        onSubmit={e => {
          void handleSubmit(onSubmit)(e);
        }}
        className="flex gap-2 items-start"
      >
        <div className="flex-1 max-w-sm">
          <input
            {...register('domain')}
            placeholder="acme.com"
            className="w-full border border-default rounded px-3 py-2 text-sm bg-content1 text-foreground placeholder:text-default-400 focus:outline-none focus:ring-1 focus:ring-primary"
          />
          {errors.domain && <p className="text-danger text-xs mt-1">{errors.domain.message}</p>}
        </div>
        <button
          type="submit"
          disabled={claimMutation.isPending}
          className="px-4 py-2 text-sm bg-primary text-white rounded hover:bg-primary/90 disabled:opacity-50 whitespace-nowrap"
        >
          {claimMutation.isPending ? 'Claiming…' : 'Claim domain'}
        </button>
      </form>

      {claimMutation.isError && (
        <p className="text-sm text-danger-600 dark:text-danger-400">
          {claimMutation.error?.message ?? 'Failed to claim domain.'}
        </p>
      )}

      {/* Domain list */}
      {isLoading && <p className="text-sm text-default-400">Loading…</p>}

      {data && data.domains.length === 0 && !isLoading && (
        <p className="text-sm text-default-400">
          No domains claimed yet. Enter a domain above to get started.
        </p>
      )}

      <div className="space-y-3">
        {data?.domains.map(d => (
          <DomainCard key={d.id} domain={d} orgId={orgId} />
        ))}
      </div>
    </div>
  );
}
